import { Button, Card, CardContent, CardHeader, Grid, Typography, Chip, Box, Divider } from "@mui/material";
import { useState } from "react";
import { apiConstants, getGeneric, postGeneric } from '../../common/constants'
import * as React from 'react'
import PageTitle from "../../components/PageTitle";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { useSnackbar } from "notistack";
import Loader from "../../common/Loader";
import { useForm } from "react-hook-form";
import { FormProvider, RHFSelectField } from "../../common/hook-form";
import { LoadingButton } from "@mui/lab";
import { RHFTextField } from "../../common/hook-form";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import { formatDate } from "../../helpers/UtilityHelper";
import CallingFilterFields, {
    CALLING_FILTER_FORM_DEFAULTS,
    applyCallingFilterToForm,
    buildCallingFilterPayload,
    callingFilterColumn,
} from "./CallingFilterFields";

const columns = [
    {
        field: 'push_lead',
        headerName: 'Push Lead',
        width: 120,
        flex: 0.6,
        renderCell: (col) => (
            <Chip
                label={col?.row?.push_lead ? 'Enabled' : 'Disabled'}
                color={col?.row?.push_lead ? 'success' : 'error'}
                size="small"
                variant="filled"
            />
        )
    },
    {
        field: 'tele_project_id',
        headerName: 'Project ID',
        width: 200,
        flex: 1,
    },
    {
        field: 'lastModifiedDate',
        headerName: 'Last Modified',
        width: 180,
        flex: 0.8,
        renderCell: (params) => formatDate(params?.row?.lastModifiedDate)
    },
    {
        field: "modifiedByUser",
        headerName: "Modified By",
        width: 200,
        flex: 1,
    },
]

function CallingSection({ title, description, configData, methods, onSubmit, sizePerPage, setSizePerPage, page, setPage, filterFields, gridColumns }) {
    const hasConfig = configData?.length > 0;
    const cols = gridColumns || columns;

    return (
        <Card sx={{ mb: 2 }}>
            <CardHeader
                title={
                    <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
                }
                subheader={description}
                subheaderTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
            />
            <Divider />
            <CardContent>
                <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <RHFSelectField
                                width="100%"
                                name="push_lead"
                                label="Push Lead"
                                options={[
                                    { value: true, label: 'Enable' },
                                    { value: false, label: 'Disable' }
                                ]}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <RHFTextField name="tele_project_id" label="Project ID" color="secondary" />
                        </Grid>
                    </Grid>
                    {filterFields}
                    {/* Save sits after ALL fields (incl. the filter) so it isn't missed */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <LoadingButton type="submit" variant="contained" size="small">
                            Save
                        </LoadingButton>
                    </Box>
                </FormProvider>

                {hasConfig && (
                    <Box sx={{ mt: 2 }}>
                        <ProzoDataGrid
                            columns={cols}
                            rows={configData.map((val, index) => ({ ...val, id: index }))}
                            autoHeight={true}
                            hideFooterPagination={true}
                            sizePerPage={sizePerPage}
                            setSizePerPage={setSizePerPage}
                            setPage={setPage}
                            page={page}
                            paginationMode={'client'}
                            getRowId={(row) => row.id}
                            rowCount={configData?.length || []}
                        />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

function FutworkConfig() {
    const [ndrSizePerPage, setNdrSizePerPage] = useState(10);
    const [ndrPage, setNdrPage] = useState(0);
    const [nprSizePerPage, setNprSizePerPage] = useState(10);
    const [nprPage, setNprPage] = useState(0);
    const [ndrConfigData, setNdrConfigData] = useState([]);
    const [nprConfigData, setNprConfigData] = useState([]);
    const [isData, setIsData] = useState(false);
    const [loading, setLoading] = useState(false);
    const [merchantOid, setMerchantOid] = useState(null);
    // Merchant-level "Use Netra" flag: score criteria in the filter are only allowed when it is on.
    const [netraEnabled, setNetraEnabled] = useState(true);
    const { enqueueSnackbar } = useSnackbar();

    const merchantMethods = useForm({ defaultValues: { merchant: '' } });
    const ndrMethods = useForm({ defaultValues: { push_lead: '', tele_project_id: '', ...CALLING_FILTER_FORM_DEFAULTS } });
    const nprMethods = useForm({ defaultValues: { push_lead: '', tele_project_id: '' } });

    const merchantFilter = () => {
        const id = merchantMethods.getValues().merchant;
        if (id) {
            setLoading(true);
            setMerchantOid(id);
            getGeneric(`${apiConstants.MERCHANT_DETAILS}/${id}`).then((res) => {
                setIsData(true);
                setNdrConfigData(res.data?.futworksNdrConfig ? [res.data.futworksNdrConfig] : []);
                setNprConfigData(res.data?.futworksNprConfig ? [res.data.futworksNprConfig] : []);
                // Prefill so a save that only touches the filter round-trips the existing values
                // (the backend overwrites push_lead with whatever the payload deserializes to).
                ndrMethods.setValue('push_lead', res.data?.futworksNdrConfig?.push_lead ?? '');
                ndrMethods.setValue('tele_project_id', res.data?.futworksNdrConfig?.tele_project_id ?? '');
                applyCallingFilterToForm(ndrMethods.setValue, res.data?.futworksNdrConfig?.calling_filter);
                // Prefill the NPR editable fields too (previously only the read-only grid was populated).
                nprMethods.setValue('push_lead', res.data?.futworksNprConfig?.push_lead ?? '');
                nprMethods.setValue('tele_project_id', res.data?.futworksNprConfig?.tele_project_id ?? '');
                setLoading(false);
            }).catch((err) => {
                setLoading(false);
                console.log(err);
            });
            getGeneric(`${apiConstants.MERCHANT}/${id}`)
                .then((res) => setNetraEnabled(!!res.data?.useNetra))
                .catch(() => setNetraEnabled(true)); // unknown -> let the backend decide
        }
    }

    const onNdrSubmit = (data) => {
        const hadFilter = !!(ndrConfigData[0]?.calling_filter);
        const { error, callingFilter } = buildCallingFilterPayload(data, hadFilter);
        if (error) {
            enqueueSnackbar(error, { variant: 'error' });
            return;
        }
        const payload = { push_lead: data.push_lead, tele_project_id: data.tele_project_id };
        if (callingFilter !== undefined) {
            payload.calling_filter = callingFilter;
        }
        setLoading(true);
        postGeneric(`${apiConstants.FUTWORKS_CONFIG}?merchantOid=${merchantOid}`, payload)
            .then((res) => {
                setLoading(false);
                if (res.data?.status === "SUCCESS") {
                    enqueueSnackbar("NDR config updated", { variant: 'success' });
                    merchantFilter();
                } else {
                    enqueueSnackbar(res.data?.errorMsg || "Some error occurred", { variant: 'error' });
                }
            })
            .catch(() => {
                setLoading(false);
                enqueueSnackbar("Some error occurred", { variant: 'error' });
            });
    }

    const onNprSubmit = (data) => {
        const payload = { push_lead: data.push_lead, tele_project_id: data.tele_project_id };
        setLoading(true);
        postGeneric(`${apiConstants.FUTWORKS_NPR_CONFIG}?merchantOid=${merchantOid}`, payload)
            .then((res) => {
                setLoading(false);
                if (res.data?.status === "SUCCESS") {
                    enqueueSnackbar("NPR config updated", { variant: 'success' });
                    merchantFilter();
                } else {
                    enqueueSnackbar(res.data?.errorMsg || "Some error occurred", { variant: 'error' });
                }
            })
            .catch(() => {
                setLoading(false);
                enqueueSnackbar("Some error occurred", { variant: 'error' });
            });
    }

    const handleReset = () => {
        merchantMethods.reset();
        ndrMethods.reset();
        nprMethods.reset();
        setNdrConfigData([]);
        setNprConfigData([]);
        setIsData(false);
        setMerchantOid(null);
    }

    return (
        <div>
            {loading && <Loader />}
            <PageTitle>Futworks Configuration</PageTitle>
            <Grid sx={{ paddingBottom: 2 }}>

                {/* Merchant Selector */}
                <Card sx={{ mb: 2 }}>
                    <CardHeader
                        title="Futworks Configuration"
                        subheader="Configure NDR and NPR calling automation via Futworks for a merchant"
                        titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                        subheaderTypographyProps={{ variant: 'body2' }}
                    />
                    <Divider />
                    <CardContent>
                        <FormProvider methods={merchantMethods}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <MerchantFilterCached name="merchant" label="Select Merchant" />
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <LoadingButton variant="contained" size="small" onClick={() => merchantFilter()}>
                                        Search
                                    </LoadingButton>
                                    <Button sx={{ ml: 1 }} variant="outlined" color="error" size="small" onClick={handleReset}>
                                        Reset
                                    </Button>
                                </Grid>
                            </Grid>
                            {!isData && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                                    Please select a merchant to view details
                                </Typography>
                            )}
                        </FormProvider>
                    </CardContent>
                </Card>

                {/* NDR & NPR Sections */}
                {isData && (
                    <>
                        <CallingSection
                            title="NDR Calling"
                            description="Push NDR leads to Futworks for automated calling"
                            configData={ndrConfigData}
                            methods={ndrMethods}
                            onSubmit={onNdrSubmit}
                            sizePerPage={ndrSizePerPage}
                            setSizePerPage={setNdrSizePerPage}
                            page={ndrPage}
                            setPage={setNdrPage}
                            filterFields={<CallingFilterFields netraEnabled={netraEnabled} />}
                            gridColumns={[...columns.slice(0, 2), callingFilterColumn, ...columns.slice(2)]}
                        />
                        <CallingSection
                            title="NPR Calling"
                            description="Push NPR leads to Futworks for automated calling"
                            configData={nprConfigData}
                            methods={nprMethods}
                            onSubmit={onNprSubmit}
                            sizePerPage={nprSizePerPage}
                            setSizePerPage={setNprSizePerPage}
                            page={nprPage}
                            setPage={setNprPage}
                        />
                    </>
                )}
            </Grid>
        </div>
    );
}
export default FutworkConfig;
